import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
/**
 * State of the waterfall feed: current sort tab, loaded image sets, pagination,
 * and pending-set polling.
 *
 * The feed is a list of `waterfall-sets` (one card per upload); each card
 * aggregates its images' counters and opens the lightbox with the whole set.
 */
export default class WaterfallState {
  sort = 'latest';
  sets = [];
  loading = false;
  loadingMore = false;
  hasMore = true;
  offset = 0;
  initialLoaded = false;

  /**
   * The `include` list every feed request shares (initial page, next pages,
   * pending polls). Built once: the settings it depends on cannot change
   * without a reload, so rebuilding the string per request only repeated work.
   *
   * `images` is the slideshow window; it is requested only when the slideshow
   * is enabled, and the poll needs it too so a freshly uploaded set gets its
   * slideshow images the moment it publishes.
   */

  pollTimer = null;
  pollStartedAt = 0;
  refreshing = false;
  pollFailures = 0;
  visibilityHandler = null;

  /** Give up polling after this long to avoid hammering the API forever. */
  static POLL_TIMEOUT = 300000;
  constructor() {
    this.perPage = app.forum.attribute('waterfallPerPage') || 24;
    this.pollInterval = Math.max(2, app.forum.attribute('waterfallPollInterval') || 5) * 1000;
    const include = ['user', 'coverImage'];
    if ((app.forum.attribute('waterfallSlideshowImages') || 0) >= 2) {
      include.push('images');
    }
    this.feedInclude = include.join(',');
  }
  requestParams(extra) {
    if (extra === void 0) {
      extra = {};
    }
    return {
      include: this.feedInclude,
      sort: this.sort === 'latest' ? '-created_at' : '-score',
      ...extra
    };
  }

  /**
   * Initial load. Uses the server-preloaded first page (from the /waterfall
   * content renderer) when the active tab matches its sort.
   */
  load() {
    if (!this.initialLoaded && this.sort === 'latest') {
      const preloaded = app.preloadedApiDocument();
      if (preloaded) {
        this.sets = preloaded;
        this.hasMore = preloaded.length >= this.perPage;
        this.offset = preloaded.length;
        this.initialLoaded = true;
        this.startPollingIfNeeded();
        return;
      }
    }
    this.loading = true;
    app.store.find('waterfall-sets', this.requestParams({
      page: {
        offset: 0,
        limit: this.perPage
      }
    })).then(sets => {
      this.sets = sets;
      this.offset = sets.length;
      this.hasMore = sets.length >= this.perPage;
      this.initialLoaded = true;
    }).catch(error => this.showError(error, 'lcoy-waterfall.forum.grid.load_failed')).then(() => {
      this.loading = false;
      this.startPollingIfNeeded();
      m.redraw();
    });
  }

  /**
   * Load the next page (infinite scroll).
   */
  loadNext() {
    if (this.loadingMore || !this.hasMore || this.loading) {
      return;
    }
    this.loadingMore = true;
    app.store.find('waterfall-sets', this.requestParams({
      page: {
        offset: this.offset,
        limit: this.perPage
      }
    })).then(sets => {
      // Merge, skipping sets already loaded (an upload may have shifted
      // offsets or appeared at the top).
      const existing = new Set(this.sets.map(set => set.id()));
      this.sets = this.sets.concat(sets.filter(set => !existing.has(set.id())));
      this.offset += sets.length;
      this.hasMore = sets.length >= this.perPage;
    }).catch(error => this.showError(error, 'lcoy-waterfall.forum.grid.load_failed')).then(() => {
      this.loadingMore = false;
      m.redraw();
    });
  }

  /**
   * Switch the sort tab and reload the feed.
   */
  setSort(sort) {
    if (this.sort === sort) {
      return;
    }
    this.sort = sort;
    this.sets = [];
    this.hasMore = true;
    this.offset = 0;
    this.initialLoaded = false;
    this.load();
  }

  /**
   * Sets still awaiting their images' image host transfer.
   */
  pendingSets() {
    return this.sets.filter(set => set.status() === 'pending');
  }

  /**
   * Replace the array identity so the grid re-renders. The store mutates the
   * models in place, so `sets` would otherwise be the very array the grid has
   * already rendered — and Mithril would skip the update.
   */
  touch() {
    this.sets = this.sets.slice();
  }

  /**
   * Poll the API for the pending sets until they resolve. The interval is
   * configurable in the admin panel. Polling also stops after a timeout, and a
   * visibilitychange listener catches up when the tab regains focus.
   */
  startPollingIfNeeded() {
    if (this.pollTimer || this.pendingSets().length === 0) {
      return;
    }
    this.pollStartedAt = Date.now();
    this.pollTimer = setInterval(() => {
      if (Date.now() - this.pollStartedAt > WaterfallState.POLL_TIMEOUT) {
        // A no-op when nothing is still pending; either way the poll is over.
        this.failStalePending();
        this.stopPolling();
        return;
      }

      // refreshPending() stops the poll by itself once nothing is pending, so
      // the pending list is filtered in exactly one place per tick instead of
      // once to check and again to fetch.
      this.refreshPending();
    }, this.pollInterval);
    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          this.refreshPending();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }
  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Batch-fetch every pending set in one request. The store mutates the
   * existing model instances in place, so a redraw is enough to swap the
   * "processing" card for the finished one.
   */
  refreshPending() {
    if (this.refreshing) {
      return;
    }
    const pending = this.pendingSets();
    if (pending.length === 0) {
      this.stopPolling();
      return;
    }
    const ids = pending.map(set => set.id()).filter(id => !!id);
    if (ids.length === 0) {
      return;
    }
    this.refreshing = true;
    app.store.find('waterfall-sets', ids, {
      include: this.feedInclude
    }).then(() => {
      // The store mutated the pending models in place (status and cover).
      this.touch();
      this.pollFailures = 0;
      m.redraw();
    }).catch(() => {
      // Do not swallow poll failures silently: after a few consecutive
      // failures the user is told the status could not be refreshed, rather
      // than watching a "processing" badge that never resolves.
      this.pollFailures += 1;
      if (this.pollFailures === 3) {
        app.alerts.show({
          type: 'error'
        }, extractText(app.translator.trans('lcoy-waterfall.forum.grid.poll_failed')));
      }
    }).then(() => {
      this.refreshing = false;
    });
  }

  /**
   * Polling gave up while sets were still pending: the queue job may have been
   * lost (worker restarted, retries exhausted without ever reporting a failed
   * status). Mark them failed locally so the user gets a definitive error
   * instead of an endless "processing" badge. Reloading reconciles with the
   * server if the transfer did eventually finish.
   */
  failStalePending() {
    const stale = this.pendingSets();
    if (stale.length === 0) {
      return;
    }
    const message = extractText(app.translator.trans('lcoy-waterfall.forum.card.timeout_error'));
    stale.forEach(set => set.pushAttributes({
      status: 'failed'
    }));
    this.touch();
    app.alerts.show({
      type: 'error'
    }, message);
    m.redraw();
  }

  /**
   * Surface an API failure: prefer the server-provided alert (rate limit,
   * permission, validation) and fall back to a generic message.
   */
  showError(error, fallbackKey) {
    const alert = error?.alert;
    if (alert) {
      app.alerts.show(alert, alert.content);
      return;
    }
    app.alerts.show({
      type: 'error'
    }, extractText(app.translator.trans(fallbackKey)));
  }

  /**
   * Called after a set is created (before its images are uploaded): prepend it
   * to the feed and start polling so the card resolves when processing ends.
   */
  addUploadedSet(set) {
    this.sets = [set, ...this.sets.filter(existing => existing.id() !== set.id())];
    this.offset += 1;
    this.startPollingIfNeeded();
    m.redraw();
  }

  /**
   * Drop a set the server no longer has (deleted, or its last image was).
   *
   * `offset` counts everything fetched from the server so far, so it has to
   * shrink with the feed: the rows after the deleted one have moved up by one,
   * and leaving the offset alone would make the next page start one row too
   * far in — silently skipping a set the user never sees.
   */
  removeSet(set) {
    if (!this.sets.some(existing => existing.id() === set.id())) {
      return;
    }
    this.sets = this.sets.filter(existing => existing.id() !== set.id());
    this.offset = Math.max(0, this.offset - 1);
  }
}
flarum.reg.add('lcoy-waterfall', 'forum/states/WaterfallState', WaterfallState);